import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { reviewService, emailService, aiService } from '../../utils/dataServices';
import supabase from '../../utils/supabase';
// Removed useOrganization - not needed for public review submission

// Import the new external review component
import ExternalReviewOptions from './components/ExternalReviewOptions';
import ContactForm from './components/ContactForm';

const ReviewSubmission = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Organization data will be loaded from token validation
  
  // Support both legacy and new token-based URLs
  const appointmentId = searchParams.get('appointment');
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [appointment, setAppointment] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [reviewRequest, setReviewRequest] = useState(null);
  const [organization, setOrganization] = useState(null); // Organization from token validation
  const [error, setError] = useState(null);
  const [resolvedServiceName, setResolvedServiceName] = useState(null);
  
  // Flow states
  const [showContactForm, setShowContactForm] = useState(false);
  const [showExternalOptions, setShowExternalOptions] = useState(false);
  const [showAIGeneratedReview, setShowAIGeneratedReview] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  
  // Campaign configuration from review request
  const [campaignConfig, setCampaignConfig] = useState(null);
  
  // Additional ratings for campaign questions
  const [additionalRatings, setAdditionalRatings] = useState({});
  
  // AI improvement states
  const [aiImproving, setAiImproving] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [improvedComment, setImprovedComment] = useState('');
  const [showImprovedVersion, setShowImprovedVersion] = useState(false);
  
  // AI Generated Review states
  const [generatedReview, setGeneratedReview] = useState('');
  const [generatingReview, setGeneratingReview] = useState(false);
  const [allowReviewEdit, setAllowReviewEdit] = useState(false);
  const [aiGenerationError, setAiGenerationError] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  
  const [reviewData, setReviewData] = useState({
    rating: 0,
    comment: '',
    reviewer_name: '',
    reviewer_email: '',
    reviewer_phone: ''
  });

  // Removed localStorage businessSettings - now using currentOrg from organization context

  useEffect(() => {
    if (token) {
      // New token-based flow
      loadReviewFromToken();
    } else if (appointmentId) {
      // Legacy appointment ID flow
      loadAppointmentDetails();
    } else {
      setError('Invalid review link. Missing review information.');
      setLoading(false);
    }
  }, [appointmentId, token]);

  const loadReviewFromToken = async () => {
    setLoading(true);
    try {
      const tokenValidation = await reviewService.validateReviewToken(token);
      
      if (!tokenValidation.success) {
        setError(tokenValidation.error || 'Invalid or expired review link.');
        setLoading(false);
        return;
      }

      const { customer, reviewRequest, organization } = tokenValidation.data;
      
      setCustomer(customer);
      setReviewRequest(reviewRequest);
      
      // Set organization data from token validation
      if (organization) {
        setOrganization(organization);
      }
      
      // Extract campaign configuration from review request
      if (reviewRequest.campaign_config) {
        setCampaignConfig(reviewRequest.campaign_config);
        console.log('📋 Loaded campaign configuration:', reviewRequest.campaign_config);
      }
      
      // Resolve service for display priority:
      // 1) review_requests.service_id → primary_services.name
      // 2) customer's primary_service.name
      // 3) parse from notes "Instant request for service: X"
      try {
        let svc = null;
        if (reviewRequest?.service_id) {
          const { data: ps } = await supabase
            .from('primary_services')
            .select('name')
            .eq('id', reviewRequest.service_id)
            .single();
          svc = ps?.name || null;
        }
        if (!svc && customer?.primary_service?.name) {
          svc = customer.primary_service.name;
        }
        if (!svc && reviewRequest?.notes?.includes('Instant request for service:')) {
          const idx = reviewRequest.notes.indexOf('Instant request for service:');
          if (idx >= 0) {
            svc = reviewRequest.notes.slice(idx + 'Instant request for service:'.length).trim();
          }
        }
        setResolvedServiceName(svc);
      } catch (e) {
        console.warn('Service resolve failed:', e);
      }

      // Pre-fill customer information
      setReviewData(prev => ({
        ...prev,
        reviewer_name: customer.name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim(),
        reviewer_email: customer.email || '',
        reviewer_phone: customer.phone || ''
      }));
      
      // Check if review already completed
      if (reviewRequest.review_completed) {
        setSubmitted(true);
        setShowThankYou(true);
      }
    } catch (error) {
      console.error('Error loading review from token:', error);
      setError('Failed to load review details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadAppointmentDetails = async () => {
    setLoading(true);
    try {
      // Appointment-based review submission is not supported in ServiceReviewAI
      // Skip appointment loading and continue with manual review submission
      if (appointmentId) {
        console.log('Appointment ID provided but appointment system is not supported in ServiceReviewAI');
        setError('Appointment-based review submission is not supported. Please use direct review submission.');
        return;
      }
      
      // Continue with normal review submission flow
      setLoading(false);
    } catch (error) {
      console.error('Error in loadAppointmentDetails:', error);
      setError('Failed to initialize review submission. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setReviewData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRatingClick = (rating) => {
    setReviewData(prev => ({
      ...prev,
      rating
    }));
  };

  const handleAdditionalRatingClick = (questionLabel, rating) => {
    setAdditionalRatings(prev => ({
      ...prev,
      [questionLabel]: rating
    }));
  };

  const renderCampaignQuestions = () => {
    if (!campaignConfig?.questions || campaignConfig.questions.length <= 1) {
      return null;
    }

    // Filter out "Overall Experience" as it's already handled above
    const additionalQuestions = campaignConfig.questions.filter(
      q => q.label !== 'Overall Experience' && q.enabled !== false
    );

    if (additionalQuestions.length === 0) {
      return null;
    }

    return (
      <div className="mb-8">
        {additionalQuestions.map((question, index) => (
          <div key={question.label} className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-3">
              {question.label} {question.required && '*'}
            </label>
            
            <div className="flex items-center justify-center space-x-2 mb-2">
              {Array.from({ length: 5 }, (_, starIndex) => {
                const starNumber = starIndex + 1;
                const isFilled = starNumber <= (additionalRatings[question.label] || 0);
                
                return (
                  <button
                    key={starNumber}
                    type="button"
                    onClick={() => handleAdditionalRatingClick(question.label, starNumber)}
                    className={`text-3xl transition-colors duration-200 ${
                      isFilled ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-300'
                    }`}
                  >
                    ★
                  </button>
                );
              })}
            </div>
            
            <div className="text-center">
              <span className="text-sm text-muted-foreground">
                {additionalRatings[question.label] 
                  ? getRatingText(additionalRatings[question.label])
                  : 'Select a rating'
                }
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // AI improvement function (optional)
  const handleImproveWithAI = async () => {
    if (!reviewData.comment.trim()) {
      setError('Please write some feedback first before improving with AI.');
      return;
    }

    setAiImproving(true);
    setAiError(null);
    setError(null);
    
    try {
      const organizationName = organization?.business_name || organization?.name || 'Your Business';
      const improveResult = await aiService.improveReview(
        reviewData.comment,
        'service',
        organizationName,
        customer?.organization_id,
        true // isPublicSubmission = true
      );
      
      if (improveResult.success) {
        setImprovedComment(improveResult.data.improved);
        setShowImprovedVersion(true);
      } else {
        setAiError(improveResult.error || 'AI improvement failed');
      }
    } catch (error) {
      console.error('AI improvement failed:', error);
      setAiError('AI improvement unavailable');
    } finally {
      setAiImproving(false);
    }
  };

  // Use improved version
  const useImprovedVersion = () => {
    setReviewData(prev => ({
      ...prev,
      comment: improvedComment
    }));
    setShowImprovedVersion(false);
  };

  // Keep original version
  const keepOriginalVersion = () => {
    setShowImprovedVersion(false);
  };

  // Generate AI review for public sharing
  const generateAIReview = async () => {
    setGeneratingReview(true);
    setAiGenerationError(null);
    
    try {
      const displayInfo = getDisplayInfo();
      const organizationName = displayInfo.organizationName;
      const serviceName = displayInfo.serviceName;
      
      // Create prompt for public review generation
      const publicReviewPrompt = `Create a professional ${reviewData.rating}-star review for ${organizationName}. 
        Service: ${serviceName}. 
        ${reviewData.comment ? `Based on customer feedback: "${reviewData.comment}"` : ''}
        
        Make it:
        - Natural and authentic sounding
        - Appropriate for public review platforms
        - ${reviewData.rating === 5 ? 'Highly positive and enthusiastic' : reviewData.rating === 4 ? 'Positive and satisfied' : 'Balanced and fair'}
        - 2-3 sentences long
        - Include the business name naturally
        - Mention the service type`;
      
      const improveResult = await aiService.improveReview(
        publicReviewPrompt,
        'public',
        organizationName,
        customer?.organization_id,
        true // isPublicSubmission = true
      );
      
      if (improveResult.success) {
        setGeneratedReview(improveResult.data.improved);
      } else {
        // Fallback to customer's original comment or generic review
        const fallbackReview = reviewData.comment || 
          `${organizationName} provided excellent ${serviceName.toLowerCase()}. ${reviewData.rating === 5 ? 'Highly recommend!' : 'Would recommend to others.'}`;
        setGeneratedReview(fallbackReview);
        setAiGenerationError('AI generation failed, using fallback review');
      }
    } catch (error) {
      console.error('AI review generation failed:', error);
      // Fallback review
      const displayInfo = getDisplayInfo();
      const fallbackReview = reviewData.comment || 
        `${displayInfo.organizationName} provided excellent service. ${reviewData.rating === 5 ? 'Highly recommend!' : 'Would recommend to others.'}`;
      setGeneratedReview(fallbackReview);
      setAiGenerationError('AI generation unavailable, using fallback review');
    } finally {
      setGeneratingReview(false);
    }
  };

  // Main submit function - called when user clicks Continue
  const handleSubmitReview = async () => {
    if (reviewData.rating === 0) {
      setError('Please select a rating before continuing.');
      return;
    }

    if (!reviewData.reviewer_name.trim()) {
      setError('Please enter your name.');
      return;
    }

    setError(null);
    setSubmitting(true);
    
    try {
      let result;
      
      if (token) {
        // Use token-based submission
        result = await reviewService.submitReviewWithToken(token, {
          rating: reviewData.rating,
          comment: reviewData.comment.trim(),
          reviewer_name: reviewData.reviewer_name.trim(),
          reviewer_email: reviewData.reviewer_email.trim() || null,
          reviewer_phone: reviewData.reviewer_phone.trim() || null
        });
      } else {
        // Legacy appointment-based submission is not supported in ServiceReviewAI
        setError('Direct review submission requires a review token. Please use the link provided by your service provider.');
        return;
      }

      if (result.success) {
        setSubmitted(true);
        
        // Use campaign threshold for routing decisions
        const threshold = campaignConfig?.positive_threshold || 4;
        
        // Route based on rating and campaign threshold
        if (reviewData.rating <= 2) {
          // Low ratings: Route to contact form for private feedback
          setShowContactForm(true);
        } else if (reviewData.rating === 3) {
          // Neutral rating: Just show thank you
          setShowThankYou(true);
        } else if (reviewData.rating >= threshold) {
          // High ratings: Check if AI review is enabled
          if (campaignConfig?.ai_review_enabled !== false) {
            // Show AI Generated Review Page
            setShowAIGeneratedReview(true);
            // Generate the AI review
            generateAIReview();
          } else {
            // AI review disabled, go directly to external platform options
            setShowExternalOptions(true);
          }
        } else {
          // Below threshold but not low: Just show thank you
          setShowThankYou(true);
        }
      } else {
        setError(result.error || 'Failed to submit review. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      setError('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleContactFormSubmit = async (contactData) => {
    // Contact form submission for low ratings (review already submitted)
    setShowContactForm(false);
    setShowThankYou(true);
  };

  const handleExternalPlatformClick = (platform) => {
    console.log('handleExternalPlatformClick called with platform:', platform);
    console.log('campaignConfig:', campaignConfig);
    console.log('organization:', organization);
    
    // Use proper URL resolution hierarchy
    let platformUrl = '';
    
    // Priority order:
    // 1. Campaign-specific platform URL
    // 2. Organization default platform URL (from context, not localStorage)
    // 3. Empty string (no URL configured)
    if (campaignConfig?.platform_urls?.[platform]) {
      platformUrl = campaignConfig.platform_urls[platform];
      console.log('Using campaign URL:', platformUrl);
    } else if (organization) {
      // Fallback to organization URLs
      const orgUrls = {
        google: organization.google_review_url,
        facebook: organization.facebook_url,
        yelp: organization.yelp_url
      };
      platformUrl = orgUrls[platform] || '';
      console.log('Using organization URL:', platformUrl);
    }
    
    console.log('Final platformUrl:', platformUrl);
    
    if (platformUrl && platformUrl.trim()) {
      console.log('Opening URL:', platformUrl);
      window.open(platformUrl, '_blank');
    } else {
      console.error('No URL configured for platform:', platform);
      // Show error message to user
      setError(`No URL configured for ${platform}. Please contact your service provider.`);
    }
    
    // Show thank you after they choose a platform
    setShowExternalOptions(false);
    setShowAIGeneratedReview(false);
    setShowThankYou(true);
  };


  const copyReviewText = () => {
    navigator.clipboard.writeText(reviewData.comment);
    // Could add a toast notification here
  };

  // Copy generated AI review
  const copyGeneratedReview = async () => {
    try {
      await navigator.clipboard.writeText(generatedReview);
      setCopySuccess(true);
      // Reset success message after 2 seconds
      setTimeout(() => setCopySuccess(false), 2000);
      return true;
    } catch (error) {
      console.error('Failed to copy review:', error);
      return false;
    }
  };

  const renderStars = (interactive = true) => {
    return Array.from({ length: 5 }, (_, index) => {
      const starNumber = index + 1;
      const isFilled = starNumber <= reviewData.rating;
      
      if (!interactive) {
        return (
          <span
            key={starNumber}
            className={`text-2xl ${isFilled ? 'text-yellow-400' : 'text-gray-300'}`}
          >
            ★
          </span>
        );
      }
      
      return (
        <button
          key={starNumber}
          type="button"
          onClick={() => handleRatingClick(starNumber)}
          className={`text-4xl sm:text-5xl transition-colors duration-200 min-h-[56px] min-w-[56px] flex items-center justify-center rounded-lg hover:bg-gray-50 mobile-touch-optimized ${
            isFilled ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-300'
          }`}
        >
          ★
        </button>
      );
    });
  };

  const getRatingText = (rating) => {
    switch (rating) {
      case 1: return 'Very Poor';
      case 2: return 'Poor';
      case 3: return 'Average';
      case 4: return 'Good';
      case 5: return 'Excellent';
      default: return 'Select a rating';
    }
  };

  const getDisplayInfo = () => {
    // Determine service name with fallback logic
    let serviceName = resolvedServiceName || 'Service Completed';
    if (!serviceName) {
      if (customer?.primary_service?.name) {
        serviceName = customer.primary_service.name;
      } else if (organization?.business_type) {
        serviceName = organization.business_type;
      }
    }
    
    // Determine organization name from context
    const organizationName = organization?.business_name || organization?.name || 'Your Business';
    
    if (customer && reviewRequest) {
      return {
        serviceName,
        organizationName
      };
    }
    
    // Appointment-based flow is deprecated in ServiceReviewAI
    
    return {
      serviceName,
      organizationName
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading review details...</p>
        </div>
      </div>
    );
  }

  if (submitted && showThankYou) {
    const displayInfo = getDisplayInfo();
    
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Helmet>
          <title>Review Submitted - ServiceFlow Pro</title>
        </Helmet>
        
        <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="CheckCircle" size={32} className="text-green-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-foreground mb-4">
            Thank You!
          </h1>
          
          <p className="text-muted-foreground mb-6">
            {reviewData.rating <= 2 
              ? "Thank you for your feedback. We'll be in touch soon to make things right."
              : "Your review has been submitted successfully. We appreciate your feedback and will use it to continue improving our services."
            }
          </p>
          
          {reviewData.rating > 0 && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex justify-center mb-2">
                {renderStars(false)}
              </div>
              <p className="text-sm text-foreground mb-2">
                <strong>Service:</strong> {displayInfo.serviceName}
              </p>
              <p className="text-sm text-foreground">
                <strong>Team:</strong> {displayInfo.organizationName}
              </p>
            </div>
          )}
          
          <Button
            variant="ghost"
            onClick={() => window.close()}
            className="text-muted-foreground"
          >
            Close Window
          </Button>
        </div>
      </div>
    );
  }

  if (error && !customer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Helmet>
          <title>Review Submission Error - ServiceFlow Pro</title>
        </Helmet>
        
        <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="XCircle" size={32} className="text-red-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-foreground mb-4">
            Unable to Load Review
          </h1>
          
          <p className="text-muted-foreground mb-6">
            {error}
          </p>
          
          <Button
            variant="ghost"
            onClick={() => window.close()}
            className="text-muted-foreground"
          >
            Close Window
          </Button>
        </div>
      </div>
    );
  }

  // Contact Form Component (for low ratings) - review already submitted
  if (showContactForm) {
    return (
      <ContactForm
        customer={customer}
        reviewData={reviewData}
        onSubmit={handleContactFormSubmit}
        onBack={() => {
          setShowContactForm(false);
          setShowThankYou(true);
        }}
        submitting={false}
        error={null}
      />
    );
  }

  // AI Generated Review Component (for high ratings) - review already submitted
  if (showAIGeneratedReview) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <Helmet>
          <title>Share Your Experience - ServiceFlow Pro</title>
        </Helmet>
        
        <div className="max-w-2xl mx-auto">
          {/* Fixed Header */}
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">🎉</div>
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Feedback Submitted!
            </h1>
            <p className="text-muted-foreground">
              {campaignConfig?.thank_you_pages?.high?.message || 
               "We've drafted a review using AI and your feedback. Feel free to copy and customize it, then share it on your preferred platform."}
            </p>
          </div>

          {/* AI Generated Review Card */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                <Icon name="Zap" size={16} className="text-white" />
              </div>
              <span className="font-semibold text-blue-900">AI-Crafted Review</span>
            </div>
            
            {generatingReview ? (
              <div className="bg-card rounded-lg p-6 border shadow-sm">
                <div className="flex items-center justify-center py-8">
                  <div className="text-center space-y-3">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                    <p className="text-muted-foreground">Crafting your review...</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-card rounded-lg p-4 border shadow-sm">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: reviewData.rating }, (_, i) => (
                    <Icon key={i} name="Star" size={16} className="text-yellow-400 fill-current" />
                  ))}
                </div>
                
                {allowReviewEdit ? (
                  <div className="space-y-3">
                    <textarea
                      value={generatedReview}
                      onChange={(e) => setGeneratedReview(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={4}
                    />
                    <div className="flex gap-2 justify-end">
                      <Button 
                        variant="ghost"
                        size="sm"
                        onClick={() => setAllowReviewEdit(false)}
                      >
                        Cancel
                      </Button>
                      <Button 
                        variant="primary"
                        size="sm"
                        onClick={() => setAllowReviewEdit(false)}
                      >
                        Save Changes
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-foreground leading-relaxed">{generatedReview}</p>
                    <div className="flex justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setAllowReviewEdit(true)}
                        className="flex items-center text-muted-foreground hover:text-blue-600"
                      >
                        <Icon name="Edit" size={14} className="mr-1" />
                        Edit Review
                      </Button>
                    </div>
                  </div>
                )}
                
                {aiGenerationError && (
                  <div className="mt-2 text-xs text-yellow-600 bg-yellow-50 p-2 rounded">
                    {aiGenerationError}
                  </div>
                )}
              </div>
            )}

            {!generatingReview && (
              <div className="mt-4 flex flex-col items-center">
                <Button
                  variant="primary"
                  onClick={copyGeneratedReview}
                  className="flex items-center"
                >
                  <Icon name={copySuccess ? "Check" : "Copy"} size={16} className="mr-2" />
                  {copySuccess ? "Copied!" : "Copy Review"}
                </Button>
                {copySuccess && (
                  <p className="text-green-600 text-sm mt-2 font-medium">Review copied to clipboard!</p>
                )}
              </div>
            )}
          </div>

          {/* External Platform Button */}
          <div className="text-center mt-6">
            <Button
              variant="primary"
              onClick={() => handleExternalPlatformClick(campaignConfig?.selected_review_platform || 'google')}
              className="w-full py-4 text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
            >
              <Icon name="ExternalLink" size={20} className="mr-3" />
              Post Review on {campaignConfig?.selected_review_platform === 'google' ? 'Google' : 
                            campaignConfig?.selected_review_platform === 'yelp' ? 'Yelp' : 
                            campaignConfig?.selected_review_platform === 'facebook' ? 'Facebook' : 'Google'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // External Review Options Component (for high ratings) - review already submitted
  if (showExternalOptions) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <Helmet>
          <title>Share Your Experience - ServiceFlow Pro</title>
        </Helmet>
        
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-green-500 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Icon name="Heart" size={32} className="text-white" />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Thanks for the {reviewData.rating}-star review!
            </h1>
            <p className="text-muted-foreground">
              Would you like to share your positive experience publicly to help others find us?
            </p>
          </div>

          {/* Review Summary */}
          <div className="bg-card rounded-lg shadow-sm border p-6 mb-8">
            <div className="text-center mb-4">
              <div className="flex justify-center mb-2">
                {renderStars(false)}
              </div>
              <h3 className="font-semibold text-foreground">Your Review</h3>
            </div>
            
            {reviewData.comment && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-foreground italic mb-4">"{reviewData.comment}"</p>
                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    onClick={copyReviewText}
                    className="flex items-center"
                  >
                    <Icon name="Copy" size={16} className="mr-2" />
                    Copy Review
                  </Button>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
              <div>
                <strong>Service:</strong> {getDisplayInfo().serviceName}
              </div>
              <div>
                <strong>Team:</strong> {getDisplayInfo().organizationName}
              </div>
              <div>
                <strong>Customer:</strong> {reviewData.reviewer_name}
              </div>
            </div>
          </div>

          {/* Platform Options */}
          <div className="bg-card rounded-lg shadow-sm border p-6 mb-8">
            <h3 className="text-lg font-semibold text-foreground mb-4">Share on External Platforms</h3>
            
            <div className="space-y-3">
              {organization?.google_review_url && (
                <Button
                  variant="outline"
                  onClick={() => handleExternalPlatformClick('google')}
                  className="w-full flex items-center justify-center bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                >
                  <Icon name="Star" size={20} className="mr-2" />
                  Post on Google Reviews
                </Button>
              )}
              
              {organization?.facebook_url && (
                <Button
                  variant="outline"
                  onClick={() => handleExternalPlatformClick('facebook')}
                  className="w-full flex items-center justify-center bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                >
                  <Icon name="ThumbsUp" size={20} className="mr-2" />
                  Post on Facebook
                </Button>
              )}
              
              {organization?.yelp_url && (
                <Button
                  variant="outline"
                  onClick={() => handleExternalPlatformClick('yelp')}
                  className="w-full flex items-center justify-center bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                >
                  <Icon name="MessageSquare" size={20} className="mr-2" />
                  Post on Yelp
                </Button>
              )}
            </div>
          </div>

          {/* No Thanks Option */}
          <div className="text-center">
            <Button
              variant="ghost"
              onClick={() => {
                setShowExternalOptions(false);
                setShowThankYou(true);
              }}
              className="text-muted-foreground"
            >
              No Thanks
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const displayInfo = getDisplayInfo();
  
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <Helmet>
        <title>Submit Review - ServiceFlow Pro</title>
        <meta name="description" content="Share your experience with our service" />
      </Helmet>
      
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          {organization?.logo_url ? (
            <div className="flex justify-center mb-4">
              <img 
                src={organization.logo_url} 
                alt={organization.business_name || organization.name}
                className="h-16 w-auto max-w-48 object-contain"
              />
            </div>
          ) : (
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-foreground">
                {organization?.business_name || organization?.name || 'Your Business'}
              </h2>
            </div>
          )}
          <h1 className="text-3xl font-bold text-foreground mb-2">
            How was our service?
          </h1>
          <p className="text-muted-foreground">
            Your feedback helps us improve and serve you better
          </p>
        </div>

        {/* Service Details Card */}
        <div className="bg-card rounded-lg shadow-sm border p-6 mb-8">
          <h2 className="text-lg font-semibold text-foreground mb-4">Service Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Service</p>
              <p className="font-medium text-foreground">{displayInfo.serviceName}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Team</p>
              <p className="font-medium text-foreground">{displayInfo.organizationName}</p>
            </div>
          </div>
        </div>

        {/* Review Form */}
        <div className="bg-card rounded-lg shadow-sm border p-6">
          {/* Rating Section */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-foreground mb-4">
              Overall Rating *
            </label>
            
            <div className="flex items-center justify-center space-x-1 sm:space-x-2 mb-4 px-4">
              {renderStars()}
            </div>
            
            <div className="text-center">
              <span className="text-lg font-medium text-foreground">
                {getRatingText(reviewData.rating)}
              </span>
            </div>
          </div>

          {/* Campaign Questions */}
          {renderCampaignQuestions()}

          {/* Comment Section */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              Additional Comments (Optional)
            </label>
            <textarea
              value={reviewData.comment}
              onChange={(e) => handleInputChange('comment', e.target.value)}
              placeholder="Tell us about your experience..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />

            {/* Show AI Improved Version */}
            {showImprovedVersion && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">AI-Improved Version:</h4>
                <p className="text-blue-800 italic mb-3">"{improvedComment}"</p>
                <div className="flex space-x-2">
                  <Button
                    onClick={useImprovedVersion}
                    variant="primary"
                    size="sm"
                  >
                    Use This Version
                  </Button>
                  <Button
                    onClick={keepOriginalVersion}
                    variant="outline"
                    size="sm"
                  >
                    Keep Original
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Your Name *
              </label>
              <Input
                type="text"
                value={reviewData.reviewer_name}
                onChange={(e) => handleInputChange('reviewer_name', e.target.value)}
                placeholder="Enter your name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Email Address (Optional)
              </label>
              <Input
                type="email"
                value={reviewData.reviewer_email}
                onChange={(e) => handleInputChange('reviewer_email', e.target.value)}
                placeholder="Enter your email"
              />
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <Icon name="AlertCircle" size={16} className="text-red-600 mr-2" />
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-center">
            <Button
              onClick={handleSubmitReview}
              variant="primary"
              disabled={submitting || reviewData.rating === 0 || !reviewData.reviewer_name.trim()}
              className="min-w-40"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Submitting...
                </>
              ) : (
                'Continue'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewSubmission;